// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// modes.ts — three ways to play (principle #14). A mode must change how the game PLAYS, and there
// is no board here to resize, so the two axes are the ARMY and the WIN TEST:
//
//   Clutch    the plain game. Three Striders, three Vaulters, two Climbers, two Weavers and the
//             Matriarch. Six sides closed to win.
//   Retinue   one Strider becomes a Tender, who can lift a neighbouring tile OVER herself. That
//             sounds defensive and is the opposite: because she carries a tile over the top, the
//             slide rule never applies to the landing — and the last open side of a nearly-closed
//             Matriarch is precisely the cell whose two flanks are occupied, the one cell no
//             walking tile can ever enter. The Tender is the only piece in the game that can plug
//             it. She can also pull it open again, which is what makes the endgame a grind that
//             does not exist in the other two.
//   Cinch     Clutch's exact army, one word changed: FIVE closed sides is enough. That inverts the
//             game's central heuristic rather than merely shortening it — in Clutch a tile of your
//             own parked beside your own Matriarch is mildly awkward, in Cinch it is two fifths of
//             a loss, so you develop away from her instead of around her.
//
// Every mode carries exactly ELEVEN tiles a side. That is a deliberate invariant, not a
// coincidence: it keeps the table size, the phone layout, the contrast surface and the search
// envelope constant across the matrix, so a Retinue bug is a rules bug and never a size bug.
//
// The host's pick travels FROZEN inside the round start (rematch roundOpts) and a guest renders
// state().hostOpts — a setting each peer reads from its own UI is a setting two peers can disagree
// about, and here it would mean two different armies on the same board. An id off the wire is
// always modeOf()-validated with Object.hasOwn, so a wire key can never resolve a prototype member
// and hand the generator a Mode of undefined fields.

import { CLIMBER, KIND_COUNT, MATRIARCH, STRIDER, TENDER, VAULTER, WEAVER } from './kinds';

export type ModeId = 'clutch' | 'retinue' | 'cinch';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Tiles of each kind a side starts with, indexed by kind. Always sums to TILES_PER_SIDE. */
  readonly supply: readonly number[];
  /** How many of a Matriarch's six sides must be closed for her side to lose. */
  readonly surroundAt: 5 | 6;
  /** The Matriarch must be down by this, your Nth placement. */
  readonly matriarchBy: number;
  /** A hard draw backstop, so two cautious players can never hang a lockstep room forever. */
  readonly plyCap: number;
}

export const TILES_PER_SIDE = 11;

const army = (parts: Partial<Record<number, number>>): readonly number[] => {
  const s = new Array(KIND_COUNT).fill(0);
  for (const k of Object.keys(parts)) s[Number(k)] = parts[Number(k)]!;
  return s;
};

export const MODES: Record<ModeId, Mode> = {
  clutch: {
    id: 'clutch',
    name: 'Clutch',
    blurb: 'The plain game. Eleven tiles a side; close all six sides of the Matriarch.',
    supply: army({ [MATRIARCH]: 1, [STRIDER]: 3, [VAULTER]: 3, [CLIMBER]: 2, [WEAVER]: 2 }),
    surroundAt: 6,
    matriarchBy: 4,
    plyCap: 180,
  },
  retinue: {
    id: 'retinue',
    name: 'Retinue',
    blurb: 'A Strider becomes a Tender, who lifts a neighbour over herself — she can plug the one gap nothing else reaches, or pull it open again.',
    supply: army({ [MATRIARCH]: 1, [STRIDER]: 2, [VAULTER]: 3, [CLIMBER]: 2, [WEAVER]: 2, [TENDER]: 1 }),
    surroundAt: 6,
    matriarchBy: 4,
    plyCap: 220,
  },
  cinch: {
    id: 'cinch',
    name: 'Cinch',
    blurb: 'The same eleven tiles, but five closed sides is enough. Build away from your Matriarch, not around her.',
    supply: army({ [MATRIARCH]: 1, [STRIDER]: 3, [VAULTER]: 3, [CLIMBER]: 2, [WEAVER]: 2 }),
    surroundAt: 5,
    matriarchBy: 4,
    plyCap: 140,
  },
};

export const MODE_IDS: readonly ModeId[] = ['clutch', 'retinue', 'cinch'];
export const DEFAULT_MODE: ModeId = 'clutch';

/** Validate an id that may have come off the wire or out of storage. Never returns undefined. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id as ModeId];
  return MODES[DEFAULT_MODE];
}

/** Does this mode's army contain a Tender? Drives whether the UI ever needs the two-stage lift. */
export const hasTender = (m: Mode): boolean => m.supply[TENDER] > 0;
